import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { platformAdminAPI } from '../../services/api';

const PLAN_OPTIONS = ['FREE', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE'];

export default function TenantDetail() {
  const { id } = useParams();
  const [tenant, setTenant] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [togglingUserId, setTogglingUserId] = useState(null);
  const [statusToggling, setStatusToggling] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);

  // Plan form state
  const [planName, setPlanName] = useState('');
  const [quoteLimit, setQuoteLimit] = useState('');
  const [seatLimit, setSeatLimit] = useState('');
  const [customPlanName, setCustomPlanName] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [grantNote, setGrantNote] = useState('');
  // Billing period overrides — empty string means "don't touch" on save.
  const [currentPeriodUsage, setCurrentPeriodUsage] = useState('');
  const [currentPeriodStart, setCurrentPeriodStart] = useState('');
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState('');

  // Audit history
  const [auditEvents, setAuditEvents] = useState([]);

  useEffect(() => {
    loadTenant();
  }, [id]);

  const loadTenant = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await platformAdminAPI.getTenant(id);
      const data = res.data || res;
      setTenant(data.tenant);
      setSubscription(data.subscription || null);
      setUsers(data.users || []);

      // Populate form from subscription data (plan fields live on subscription, not tenant)
      const sub = data.subscription;
      setPlanName(sub?.planTier || 'FREE');
      setQuoteLimit(String(sub?.quoteLimit ?? 10));
      setSeatLimit(String(sub?.seatLimit ?? 2));
      setCustomPlanName(sub?.customPlanName || '');
      setAdminNotes(sub?.adminNotes || '');
      // Period overrides start blank so saving the form doesn't accidentally
      // rewrite values the admin didn't intend to touch. The current state is
      // shown read-only under Usage Stats; the inputs here are explicitly
      // "I want to change this on save".
      setCurrentPeriodUsage('');
      setCurrentPeriodStart('');
      setCurrentPeriodEnd('');

      // Load audit history in parallel — non-blocking if it fails
      try {
        const auditRes = await platformAdminAPI.listTenantAudit(id, { limit: 50 });
        setAuditEvents(auditRes.data || []);
      } catch {
        setAuditEvents([]);
      }
    } catch (err) {
      setError('Failed to load tenant: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTenantStatus = async () => {
    if (!tenant) return;
    const newStatus = tenant.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    try {
      setStatusToggling(true);
      setError(null);
      setSuccess(null);
      await platformAdminAPI.updateTenantStatus(id, newStatus);
      setSuccess(`Tenant ${newStatus === 'ACTIVE' ? 'activated' : 'suspended'} successfully`);
      await loadTenant();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update status: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setStatusToggling(false);
    }
  };

  const handleSavePlan = async () => {
    try {
      setSavingPlan(true);
      setError(null);
      setSuccess(null);
      // Only send the period override fields when the admin actually typed
      // a value. Empty input → omit the key → backend leaves the column
      // untouched. Without this gate a blank "Period start" would be
      // serialized as `""`, which the backend would reject as invalid.
      const payload = {
        planTier: planName,
        quoteLimit: parseInt(quoteLimit, 10),
        seatLimit: parseInt(seatLimit, 10),
        customPlanName: customPlanName || null,
        adminNotes: adminNotes || null,
        grantNote: grantNote || null
      };
      if (currentPeriodUsage !== '') {
        payload.currentPeriodUsage = parseInt(currentPeriodUsage, 10);
      }
      if (currentPeriodStart !== '') {
        payload.currentPeriodStart = new Date(currentPeriodStart).toISOString();
      }
      if (currentPeriodEnd !== '') {
        payload.currentPeriodEnd = new Date(currentPeriodEnd).toISOString();
      }
      await platformAdminAPI.updateTenantPlan(id, payload);
      setSuccess('Plan updated successfully');
      setGrantNote('');
      await loadTenant();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save plan: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setSavingPlan(false);
    }
  };

  const handleToggleUserStatus = async (user) => {
    if (user.role === 'OWNER') return;
    const newStatus = user.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    try {
      setTogglingUserId(user.id);
      setError(null);
      setSuccess(null);
      await platformAdminAPI.updateUserStatus(user.id, newStatus);
      setSuccess(`User ${newStatus === 'ACTIVE' ? 'activated' : 'suspended'}`);
      await loadTenant();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update user status: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    } finally {
      setTogglingUserId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const summarizePlanDiff = (before, after) => {
    if (!before || !after) return [];
    const fields = [
      ['planTier', 'Plan tier'],
      ['customPlanName', 'Custom name'],
      ['quoteLimit', 'Quote limit'],
      ['seatLimit', 'Seat limit'],
      ['adminNotes', 'Admin notes']
    ];
    const changes = [];
    for (const [key, label] of fields) {
      const b = before[key];
      const a = after[key];
      if (String(b ?? '') !== String(a ?? '')) {
        changes.push(`${label}: ${b ?? '—'} → ${a ?? '—'}`);
      }
    }
    return changes;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading">Loading tenant...</div>;
  }

  if (!tenant && !loading) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1>Tenant Not Found</h1>
          <Link to="/admin/tenants" className="btn btn-secondary">Back to Tenants</Link>
        </div>
        <p className="text-muted">No tenant found with ID: {id}</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Breadcrumb */}
      <div className="text-muted" style={{ marginBottom: '0.5rem', fontSize: '0.8rem' }}>
        <Link to="/admin" style={{ color: 'inherit' }}>Admin</Link>
        {' > '}
        <Link to="/admin/tenants" style={{ color: 'inherit' }}>Tenants</Link>
        {' > '}
        {tenant.name}
      </div>

      {/* Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h1>{tenant.name}</h1>
          <span style={{
            display: 'inline-block',
            padding: '0.2rem 0.6rem',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: 600,
            background: tenant.status === 'ACTIVE' ? 'rgba(92,122,84,0.15)' : 'rgba(139,58,58,0.15)',
            color: tenant.status === 'ACTIVE' ? '#5C7A54' : '#8B3A3A'
          }}>
            {tenant.status}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className={`btn btn-sm ${tenant.status === 'ACTIVE' ? 'btn-secondary' : 'btn-primary'}`}
            onClick={handleToggleTenantStatus}
            disabled={statusToggling}
          >
            {statusToggling ? '...' : (tenant.status === 'ACTIVE' ? 'Suspend' : 'Activate')}
          </button>
          <Link to="/admin/tenants" className="btn btn-sm btn-secondary">Back to Tenants</Link>
        </div>
      </div>

      {error && (
        <div style={{ padding: '0.75rem 1rem', marginBottom: '1rem', borderRadius: '6px', background: 'rgba(139,58,58,0.1)', border: '1px solid #8B3A3A', color: '#8B3A3A', fontSize: '0.875rem' }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ padding: '0.75rem 1rem', marginBottom: '1rem', borderRadius: '6px', background: 'rgba(92,122,84,0.1)', border: '1px solid #5C7A54', color: '#5C7A54', fontSize: '0.875rem' }}>
          {success}
        </div>
      )}

      {/* Two-panel layout */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {/* Plan Configuration */}
        <div className="form-section" style={{ flex: '1 1 400px' }}>
          <h2>Plan Configuration</h2>

          <div style={{ marginBottom: '0.75rem' }}>
            <label className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Plan Tier</label>
            <select
              className="filter-input"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              style={{ width: '100%' }}
            >
              {PLAN_OPTIONS.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div style={{ flex: 1 }}>
              <label className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Quote Limit</label>
              <input
                type="number"
                className="filter-input"
                value={quoteLimit}
                onChange={(e) => setQuoteLimit(e.target.value)}
                min="0"
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Seat Limit</label>
              <input
                type="number"
                className="filter-input"
                value={seatLimit}
                onChange={(e) => setSeatLimit(e.target.value)}
                min="0"
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div
            style={{
              marginBottom: '0.75rem',
              padding: '0.75rem',
              border: '1px dashed var(--border-default, #ccc)',
              borderRadius: 6
            }}
          >
            <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.5rem' }}>
              Billing period override
            </div>
            <div className="text-muted" style={{ fontSize: '0.7rem', marginBottom: '0.5rem' }}>
              Leave blank to keep current values. Useful for reseeding tenants
              whose period was never synced from Stripe, or zeroing usage
              after a billing dispute.
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="tenant-current-period-usage" className="text-muted" style={{ display: 'block', fontSize: '0.7rem', marginBottom: '0.25rem' }}>Reset usage to</label>
                <input
                  id="tenant-current-period-usage"
                  type="number"
                  className="filter-input"
                  value={currentPeriodUsage}
                  onChange={(e) => setCurrentPeriodUsage(e.target.value)}
                  min="0"
                  placeholder={`current: ${subscription?.currentPeriodUsage ?? 0}`}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="tenant-current-period-start" className="text-muted" style={{ display: 'block', fontSize: '0.7rem', marginBottom: '0.25rem' }}>Period start</label>
                <input
                  id="tenant-current-period-start"
                  type="datetime-local"
                  className="filter-input"
                  value={currentPeriodStart}
                  onChange={(e) => setCurrentPeriodStart(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label htmlFor="tenant-current-period-end" className="text-muted" style={{ display: 'block', fontSize: '0.7rem', marginBottom: '0.25rem' }}>Period end</label>
                <input
                  id="tenant-current-period-end"
                  type="datetime-local"
                  className="filter-input"
                  value={currentPeriodEnd}
                  onChange={(e) => setCurrentPeriodEnd(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Custom Plan Name (optional)</label>
            <input
              type="text"
              className="filter-input"
              value={customPlanName}
              onChange={(e) => setCustomPlanName(e.target.value)}
              placeholder="e.g. Enterprise Plus"
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Admin Notes</label>
            <textarea
              className="filter-input"
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Internal notes about this tenant..."
              rows={3}
              style={{ width: '100%', resize: 'vertical' }}
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label className="text-muted" style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
              Grant note (for audit log, optional)
            </label>
            <input
              type="text"
              className="filter-input"
              value={grantNote}
              onChange={(e) => setGrantNote(e.target.value)}
              placeholder="e.g. Invoice #INV-1234 paid 2026-04-30"
              style={{ width: '100%' }}
            />
            <div className="text-muted" style={{ fontSize: '0.7rem', marginTop: '0.25rem' }}>
              Logged with this change in the audit history below. Not stored on the subscription.
            </div>
          </div>

          <button
            className="btn btn-primary"
            onClick={handleSavePlan}
            disabled={savingPlan}
          >
            {savingPlan ? 'Saving...' : 'Save Plan'}
          </button>
        </div>

        {/* Usage Stats */}
        <div className="form-section" style={{ flex: '1 1 300px' }}>
          <h2>Usage Stats</h2>

          <div style={{ marginBottom: '0.75rem' }}>
            <span className="text-muted" style={{ fontSize: '0.75rem' }}>Plan Tier</span>
            <div style={{ fontWeight: 500 }}>{subscription?.customPlanName || subscription?.planTier || 'FREE'}</div>
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <span className="text-muted" style={{ fontSize: '0.75rem' }}>Quote Usage</span>
            <div style={{ fontWeight: 500 }}>
              {subscription?.currentPeriodUsage ?? 0} / {subscription?.quoteLimit ?? 0} quotes
            </div>
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <span className="text-muted" style={{ fontSize: '0.75rem' }}>Seat Usage</span>
            <div style={{ fontWeight: 500 }}>
              {users.length} / {subscription?.seatLimit ?? 0} seats
            </div>
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <span className="text-muted" style={{ fontSize: '0.75rem' }}>Country</span>
            <div style={{ fontWeight: 500 }}>{tenant.country || '-'}</div>
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <span className="text-muted" style={{ fontSize: '0.75rem' }}>Created</span>
            <div style={{ fontWeight: 500 }}>{formatDate(tenant.createdAt)}</div>
          </div>

          {subscription && (
            <div style={{ marginBottom: '0.75rem' }}>
              <span className="text-muted" style={{ fontSize: '0.75rem' }}>Subscription Status</span>
              <div style={{ fontWeight: 500 }}>{subscription.status || '-'}</div>
            </div>
          )}

          {subscription?.current_period_end && (
            <div style={{ marginBottom: '0.75rem' }}>
              <span className="text-muted" style={{ fontSize: '0.75rem' }}>Period End</span>
              <div style={{ fontWeight: 500 }}>{formatDate(subscription.current_period_end)}</div>
            </div>
          )}
        </div>
      </div>

      {/* Team Members */}
      <div className="form-section">
        <h2>Team Members ({users.length})</h2>
        <table className="simple-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: '1.5rem' }} className="text-muted">
                  No users found for this tenant.
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>{user.name || ''}</td>
                  <td>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.15rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      background: user.role === 'OWNER' ? 'rgba(43,37,96,0.1)' : 'rgba(162,162,153,0.15)',
                      color: user.role === 'OWNER' ? '#2B2560' : '#666'
                    }}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    {user.role === 'OWNER' ? (
                      <span className="text-muted" style={{ fontSize: '0.8rem' }}>{user.status}</span>
                    ) : (
                      <button
                        className="btn btn-sm"
                        style={{
                          background: user.status === 'ACTIVE' ? 'rgba(92,122,84,0.15)' : 'rgba(139,58,58,0.15)',
                          color: user.status === 'ACTIVE' ? '#5C7A54' : '#8B3A3A',
                          border: 'none',
                          cursor: 'pointer',
                          minWidth: '70px'
                        }}
                        disabled={togglingUserId === user.id}
                        onClick={() => handleToggleUserStatus(user)}
                      >
                        {togglingUserId === user.id ? '...' : user.status}
                      </button>
                    )}
                  </td>
                  <td className="text-muted" style={{ fontSize: '0.8rem' }}>
                    {formatDateTime(user.last_login_at)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Grant History & Audit */}
      <div className="form-section">
        <h2>Grant History & Audit ({auditEvents.length})</h2>
        {auditEvents.length === 0 ? (
          <div className="text-muted" style={{ padding: '1rem 0', fontSize: '0.85rem' }}>
            No audit events recorded yet. Plan grants, status changes, and user moderation actions will appear here.
          </div>
        ) : (
          <table className="simple-table">
            <thead>
              <tr>
                <th style={{ width: '170px' }}>When</th>
                <th style={{ width: '150px' }}>Event</th>
                <th>Change</th>
                <th>Note</th>
                <th style={{ width: '150px' }}>Actor</th>
              </tr>
            </thead>
            <tbody>
              {auditEvents.map(ev => {
                const planDiff = ev.event_type === 'TENANT_PLAN_UPDATED'
                  ? summarizePlanDiff(ev.before_data, ev.after_data)
                  : [];
                const eventLabel =
                  ev.event_type === 'TENANT_PLAN_UPDATED' ? 'Plan updated' :
                  ev.event_type === 'TENANT_STATUS_CHANGED' ? 'Tenant status' :
                  ev.event_type === 'USER_STATUS_CHANGED' ? 'User status' :
                  ev.event_type;
                return (
                  <tr key={ev.id}>
                    <td className="text-muted" style={{ fontSize: '0.8rem' }}>{formatDateTime(ev.created_at)}</td>
                    <td style={{ fontSize: '0.85rem' }}>{eventLabel}</td>
                    <td style={{ fontSize: '0.8rem' }}>
                      {planDiff.length > 0 ? (
                        <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                          {planDiff.map((line, i) => <li key={i}>{line}</li>)}
                        </ul>
                      ) : ev.event_type === 'TENANT_STATUS_CHANGED' && ev.before_data && ev.after_data ? (
                        <span>{ev.before_data.status} → {ev.after_data.status}</span>
                      ) : ev.event_type === 'USER_STATUS_CHANGED' && ev.after_data ? (
                        <span>{ev.after_data.email || ev.target_id} → {ev.after_data.status}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td style={{ fontSize: '0.8rem' }}>
                      {ev.note || <span className="text-muted">—</span>}
                    </td>
                    <td className="text-muted" style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                      {ev.actor_user_id ? `${String(ev.actor_user_id).slice(0, 8)}…` : '—'}
                      {ev.actor_role ? ` (${ev.actor_role})` : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
